/**
 * Recording that a user verified their email, and when.
 *
 * `users/{uid}.emailVerified` / `.emailVerifiedAt`
 *
 * ⚠️ **Firebase Auth does not store a verification timestamp.** `UserRecord`
 * carries `emailVerified` as a boolean and `metadata` with only
 * `creationTime`, `lastSignInTime` and `lastRefreshTime`. So the moment is
 * knowable only if we write it down as it happens, which is what this does —
 * and is why `scripts/backfill-email-verified.ts` can restore the *state* for
 * existing users but never the *date*.
 *
 * **An absent `emailVerifiedAt` on a verified user therefore means "verified
 * before we tracked it"**, not "never verified". Reporting must exclude those
 * rows from any time series rather than treating them as zero or as today.
 *
 * The value is the first moment the app OBSERVED the verification, which is
 * the click itself when they land back in a live session, and the next sign-in
 * when they verify on another device. It is never rewritten once set, so it
 * cannot drift later to a re-observation.
 */

import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { logError } from "@/lib/bugsnag";

/**
 * Write the verification, once.
 *
 * Reads first and returns early when `emailVerifiedAt` is already set, so the
 * stamp stays the first observation. Merging, so it can never clobber the rest
 * of the user document — unlike the non-merging `setDoc` calls on the signup
 * path.
 *
 * Never throws: a failure here must not block the redirect into the app.
 * Returns true only when it actually wrote.
 */
export async function recordEmailVerified(uid: string): Promise<boolean> {
  if (!uid) return false;
  try {
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    // No user document yet (an invite flow mid-creation, say). Writing one from
    // here would create a half-built record; the next observation catches it.
    if (!snap.exists()) return false;
    if (snap.data()?.emailVerifiedAt) return false;

    await setDoc(
      ref,
      { emailVerified: true, emailVerifiedAt: serverTimestamp() },
      { merge: true }
    );
    return true;
  } catch (error) {
    logError(error, { context: "recordEmailVerified", uid });
    return false;
  }
}
